import type { EmailSidebarDisplayPrefs } from '../components/EmailSidebar';
import type { EmailListSortBy } from './emailListSortStorage';
import { isEmailListSortBy, loadEmailListSort } from './emailListSortStorage';
import { loadEmailDetailHeaderCollapsed } from './emailDetailHeaderStorage';
import { loadDisplayPrefs } from '../components/EmailSidebar';

export interface EmailUiPreferences {
  sidebarCollapsed: boolean;
  sidebarWidth: number;
  splitterPosition: number;
  threadedView: boolean;
  listSortBy: EmailListSortBy;
  displayPrefs: EmailSidebarDisplayPrefs;
  detailHeaderCollapsed: boolean;
  bodySplitPercent: number;
  libraryFolderSplitPercent: number;
}

const STORAGE_KEY = 'ums-email-ui-preferences-v1';

const SIDEBAR_WIDTH_MIN = 180;
const SIDEBAR_WIDTH_MAX = 480;
const SPLITTER_MIN = 20;
const SPLITTER_MAX = 80;
const BODY_SPLIT_MIN = 25;
const BODY_SPLIT_MAX = 85;
export const LIBRARY_FOLDER_SPLIT_MIN = 18;
export const LIBRARY_FOLDER_SPLIT_MAX = 55;
const LIBRARY_FOLDER_SPLIT_DEFAULT = 28;
const LEGACY_LIBRARY_FOLDER_SPLIT_KEY = 'ums-library-folder-split-v1';

export function defaultEmailUiPreferences(): EmailUiPreferences {
  return {
    sidebarCollapsed: false,
    sidebarWidth: 256,
    splitterPosition: 50,
    threadedView: false,
    listSortBy: 'date',
    displayPrefs: loadDisplayPrefs(),
    detailHeaderCollapsed: false,
    bodySplitPercent: 72,
    libraryFolderSplitPercent: LIBRARY_FOLDER_SPLIT_DEFAULT,
  };
}

function clampNumber(value: unknown, min: number, max: number, fallback: number): number {
  if (typeof value !== 'number' || !Number.isFinite(value)) return fallback;
  return Math.max(min, Math.min(max, Math.round(value)));
}

function asBoolean(value: unknown, fallback: boolean): boolean {
  return typeof value === 'boolean' ? value : fallback;
}

function sanitizeDisplayPrefs(value: unknown): EmailSidebarDisplayPrefs {
  const defaults = loadDisplayPrefs();
  if (!value || typeof value !== 'object') return defaults;
  const o = value as Partial<EmailSidebarDisplayPrefs>;
  return {
    displayNewMessageButton:
      typeof o.displayNewMessageButton === 'boolean' ? o.displayNewMessageButton : defaults.displayNewMessageButton,
    displaySyncButton: typeof o.displaySyncButton === 'boolean' ? o.displaySyncButton : defaults.displaySyncButton,
    displayUnreadCountBadge:
      typeof o.displayUnreadCountBadge === 'boolean' ? o.displayUnreadCountBadge : defaults.displayUnreadCountBadge,
  };
}

function resolveLibraryFolderSplitPercent(
  value: unknown,
  fallback: number,
): number {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return clampNumber(value, LIBRARY_FOLDER_SPLIT_MIN, LIBRARY_FOLDER_SPLIT_MAX, fallback);
  }
  try {
    const legacy = localStorage.getItem(LEGACY_LIBRARY_FOLDER_SPLIT_KEY);
    if (legacy) {
      const parsed = Number.parseFloat(legacy);
      if (Number.isFinite(parsed)) {
        return clampNumber(parsed, LIBRARY_FOLDER_SPLIT_MIN, LIBRARY_FOLDER_SPLIT_MAX, fallback);
      }
    }
  } catch {
    /**/
  }
  return fallback;
}

export function sanitizeEmailUiPreferences(input: unknown): EmailUiPreferences {
  const defaults = defaultEmailUiPreferences();
  if (!input || typeof input !== 'object') return defaults;
  const o = input as Record<string, unknown>;
  const listSortBy = typeof o.listSortBy === 'string' && isEmailListSortBy(o.listSortBy) ? o.listSortBy : defaults.listSortBy;
  return {
    sidebarCollapsed: asBoolean(o.sidebarCollapsed, defaults.sidebarCollapsed),
    sidebarWidth: clampNumber(o.sidebarWidth, SIDEBAR_WIDTH_MIN, SIDEBAR_WIDTH_MAX, defaults.sidebarWidth),
    splitterPosition: clampNumber(o.splitterPosition, SPLITTER_MIN, SPLITTER_MAX, defaults.splitterPosition),
    threadedView: asBoolean(o.threadedView, defaults.threadedView),
    listSortBy,
    displayPrefs: sanitizeDisplayPrefs(o.displayPrefs),
    detailHeaderCollapsed: asBoolean(o.detailHeaderCollapsed, defaults.detailHeaderCollapsed),
    bodySplitPercent: clampNumber(o.bodySplitPercent, BODY_SPLIT_MIN, BODY_SPLIT_MAX, defaults.bodySplitPercent),
    libraryFolderSplitPercent: resolveLibraryFolderSplitPercent(
      o.libraryFolderSplitPercent,
      defaults.libraryFolderSplitPercent,
    ),
  };
}

function migrateLegacyEmailUiPreferences(): EmailUiPreferences {
  const defaults = defaultEmailUiPreferences();
  try {
    const collapsed = localStorage.getItem('emails-sidebar-collapsed') === '1';
    const widthRaw = localStorage.getItem('emails-sidebar-width');
    const width = widthRaw != null ? Number.parseInt(widthRaw, 10) : defaults.sidebarWidth;
    return {
      ...defaults,
      sidebarCollapsed: collapsed,
      sidebarWidth: clampNumber(width, SIDEBAR_WIDTH_MIN, SIDEBAR_WIDTH_MAX, defaults.sidebarWidth),
      listSortBy: loadEmailListSort(),
      displayPrefs: loadDisplayPrefs(),
      detailHeaderCollapsed: loadEmailDetailHeaderCollapsed(),
    };
  } catch {
    return defaults;
  }
}

export function loadEmailUiPreferencesFromLocal(): EmailUiPreferences {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      return sanitizeEmailUiPreferences(JSON.parse(raw));
    }
  } catch {
    /**/
  }
  return migrateLegacyEmailUiPreferences();
}

function syncLegacyLocalStorage(prefs: EmailUiPreferences): void {
  try {
    localStorage.setItem('emails-sidebar-collapsed', prefs.sidebarCollapsed ? '1' : '0');
    localStorage.setItem('emails-sidebar-width', String(prefs.sidebarWidth));
    localStorage.setItem('emails-sidebar-display-preferences', JSON.stringify(prefs.displayPrefs));
    localStorage.setItem('ums-email-list-sort-v1', prefs.listSortBy);
    localStorage.setItem('ums-email-detail-header-collapsed-v1', prefs.detailHeaderCollapsed ? 'true' : 'false');
  } catch {
    /**/
  }
}

export function saveEmailUiPreferencesToLocal(prefs: EmailUiPreferences): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
    syncLegacyLocalStorage(prefs);
  } catch {
    /**/
  }
}

export function mergeEmailUiPreferences(
  base: EmailUiPreferences,
  patch: Partial<EmailUiPreferences>,
): EmailUiPreferences {
  return sanitizeEmailUiPreferences({
    ...base,
    ...patch,
    displayPrefs: patch.displayPrefs ? { ...base.displayPrefs, ...patch.displayPrefs } : base.displayPrefs,
  });
}
